import { localizedUrl } from './seo'
import { HOTEL, RESTAURANT, WINERY } from '@/constants/hotel'
import { SITE_OG_IMAGE } from '@/constants/seo'

import type { SiteLocale } from './seo'

type Json = Record<string, unknown>

/** Drops keys whose value is `undefined` or an empty array. */
function compact(object: Json): Json {
  return Object.fromEntries(
    Object.entries(object).filter(
      ([, value]) =>
        value !== undefined && !(Array.isArray(value) && value.length === 0),
    ),
  )
}

export const hotelId = (): string => `${localizedUrl('', 'pl')}/#hotel`

export function hotelJsonLd(locale: SiteLocale): Json {
  const { latitude, longitude } = HOTEL.geo

  return compact({
    '@type': 'Hotel',
    '@id': hotelId(),
    url: localizedUrl('', locale),
    name: HOTEL.name,
    description: HOTEL.description,
    image: [SITE_OG_IMAGE.url],
    telephone: HOTEL.telephone,
    email: HOTEL.email,
    priceRange: HOTEL.priceRange,
    address: { '@type': 'PostalAddress', ...HOTEL.address },
    geo: { '@type': 'GeoCoordinates', latitude, longitude },
    hasMap: `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`,
    sameAs: [...HOTEL.sameAs, ...HOTEL.extraProfiles],
    checkinTime: HOTEL.checkinTime,
    checkoutTime: HOTEL.checkoutTime,
    petsAllowed: HOTEL.petsAllowed,
    numberOfRooms: HOTEL.numberOfRooms,
    availableLanguage: HOTEL.availableLanguage,
    amenityFeature: HOTEL.amenities.map(({ name }) => ({
      '@type': 'LocationFeatureSpecification',
      name,
      value: true,
    })),
    award: HOTEL.awards,
  })
}

export function restaurantJsonLd(locale: SiteLocale): Json {
  return compact({
    '@type': 'Restaurant',
    '@id': `${localizedUrl(RESTAURANT.path, 'pl')}#restaurant`,
    name: RESTAURANT.name,
    url: localizedUrl(RESTAURANT.path, locale),
    image: [SITE_OG_IMAGE.url],
    telephone: HOTEL.telephone,
    address: { '@type': 'PostalAddress', ...HOTEL.address },
    geo: { '@type': 'GeoCoordinates', ...HOTEL.geo },
    containedInPlace: { '@id': hotelId() },
    servesCuisine: RESTAURANT.servesCuisine,
    menu: RESTAURANT.menu,
    openingHoursSpecification: RESTAURANT.openingHoursSpecification,
  })
}

export function wineryJsonLd(locale: SiteLocale): Json {
  return compact({
    '@type': 'Winery',
    '@id': `${localizedUrl(WINERY.path, 'pl')}#winery`,
    name: WINERY.name,
    url: localizedUrl(WINERY.path, locale),
    image: [SITE_OG_IMAGE.url],
    address: { '@type': 'PostalAddress', ...HOTEL.address },
    geo: { '@type': 'GeoCoordinates', ...HOTEL.geo },
    containedInPlace: { '@id': hotelId() },
  })
}

export function breadcrumbJsonLd(trail: { name: string; url: string }[]): Json {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: trail.map(({ name, url }, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name,
      item: url,
    })),
  }
}

type BlogPostingInput = {
  url: string
  headline: string
  description: string | undefined
  image: string | undefined
  datePublished: string
  dateModified?: string | undefined
  inLanguage: SiteLocale
}

export function blogPostingJsonLd({
  url,
  headline,
  description,
  image,
  datePublished,
  dateModified,
  inLanguage,
}: BlogPostingInput): Json {
  return compact({
    '@type': 'BlogPosting',
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
    headline,
    description,
    image: [image ?? SITE_OG_IMAGE.url],
    datePublished,
    dateModified: dateModified ?? datePublished,
    inLanguage,
    author: { '@id': hotelId() },
    publisher: { '@id': hotelId() },
  })
}

type EventInput = {
  url: string
  name: string
  description: string | undefined
  startDate: string
  endDate?: string | undefined
  image: string | undefined
  place?: string | null | undefined
  placeUrl?: string | null | undefined
  placeAddress?: string | null | undefined
}

export function eventJsonLd({
  url,
  name,
  description,
  startDate,
  endDate,
  image,
  place,
  placeUrl,
  placeAddress,
}: EventInput): Json {
  return compact({
    '@type': 'Event',
    url,
    name,
    description,
    startDate,
    endDate,
    image: image ? [image] : undefined,
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: place
      ? compact({
          '@type': 'Place',
          name: place,
          url: placeUrl ?? undefined,
          address: placeAddress ?? undefined,
        })
      : {
          '@type': 'Place',
          name: HOTEL.name,
          address: { '@type': 'PostalAddress', ...HOTEL.address },
          geo: { '@type': 'GeoCoordinates', ...HOTEL.geo },
        },
    organizer: { '@id': hotelId() },
  })
}

export function faqJsonLd(items: { question: string; answer: string }[]): Json {
  return {
    '@type': 'FAQPage',
    mainEntity: items.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  }
}
